import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote, Plus, X, Loader2, Trash2, Upload, ArrowLeft, MoreVertical, Pencil, Check, ListChecks, Sparkles, CircleDot, Tags, AlertTriangle, Search, History, Layers, RotateCcw, TrendingUp, Award, Eye, Stamp } from "lucide-react";
import api from "../../lib/api";
import { BankGuide, ConsentBanner } from "../../components/guides/BankGuide";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fdate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

const CONF = {
  high: { label: "Fiabilité élevée", cls: "bg-brand-emerald/10 text-brand-emerald" },
  medium: { label: "Fiabilité moyenne", cls: "bg-amber-100 text-amber-700" },
  low: { label: "Fiabilité faible", cls: "bg-brand-soft text-brand-green/60" },
};

const autoSummary = (d) => `${d.rule_suggestions ? ` · ${d.rule_suggestions} règle(s) suggérée(s)` : ""} · ${d.auto_reconciled || 0} rapprochée(s) automatiquement · ${d.receipts_paid || 0} quittance(s) passée(s) en Payé`;

export default function Rapprochement() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [cats, setCats] = useState({ revenue: [], expense: [] });
  const [owner, setOwner] = useState("");
  const [accountName, setAccountName] = useState("");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const [editAcc, setEditAcc] = useState(null);
  const [reconAcc, setReconAcc] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [matchRulesAcc, setMatchRulesAcc] = useState(null);
  const [powens, setPowens] = useState({ configured: false, connected: false });
  const [powensBusy, setPowensBusy] = useState(false);
  const [connections, setConnections] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("");

  const load = () => api.get("/bank/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
  const loadPowens = () => api.get("/powens/status").then((r) => setPowens(r.data || {})).catch(() => {});
  const loadConnections = () => api.get("/powens/connections").then((r) => setConnections(r.data.connections || [])).catch(() => {});
  useEffect(() => {
    load();
    loadPowens();
    loadConnections();
    api.get("/owners").then((r) => setOwners(r.data || [])).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
    api.get("/transactions/categories").then((r) => setCats(r.data || { revenue: [], expense: [] })).catch(() => {});
    // Retour du Webview Powens
    const params = new URLSearchParams(window.location.search);
    if (params.get("powens") === "connected") {
      toast.success("Banque connectée — synchronisation des opérations en cours…");
      window.history.replaceState({}, "", "/app/finances/rapprochement");
      setTimeout(() => { load(); loadPowens(); loadConnections(); }, 1500);
    } else if (params.get("powens_error")) {
      toast.error(`Connexion bancaire annulée ou échouée (${params.get("powens_error")})`);
      window.history.replaceState({}, "", "/app/finances/rapprochement");
    }
  }, []);

  const connectBank = async () => {
    setPowensBusy(true);
    try {
      const r = await api.post("/powens/connect/start");
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connexion bancaire indisponible");
      setPowensBusy(false);
    }
  };

  const syncPowens = async () => {
    setPowensBusy(true);
    try {
      const r = await api.post("/powens/sync");
      toast.success(`Synchronisation terminée — ${r.data.transactions} opération(s), ${r.data.accounts} compte(s)${autoSummary(r.data)}`);
      load(); loadPowens(); loadConnections();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synchronisation impossible");
    } finally { setPowensBusy(false); }
  };

  const reconnect = async (cid) => {
    try {
      const r = await api.post(`/powens/connections/${cid}/reconnect`);
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconnexion impossible");
    }
  };

  const doImport = async () => {
    if (!file) return toast.error("Sélectionnez un fichier .ofx ou .qif");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner", owner);
      fd.append("account_name", accountName);
      const res = await api.post("/bank/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = res.data.accounts_count || 1;
      toast.success((n > 1 ? `${n} comptes importés — ${res.data.imported} opération(s)` : `${res.data.imported} opération(s) importée(s) sur « ${res.data.account.name} »`) + autoSummary(res.data));
      setFile(null); setAccountName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import impossible"); }
    finally { setImporting(false); }
  };

  const delAcc = async (a) => {
    if (!window.confirm(`Supprimer le compte « ${a.name} » et ses opérations importées ?`)) return;
    try {
      await api.delete(`/bank/accounts/${a.id}`);
      setAccounts((p) => p.filter((x) => x.id !== a.id));
      toast.success("Compte supprimé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setMenuFor(null);
  };

  return (
    <div className="space-y-6" data-testid="rapprochement-page">
      <ConsentBanner />
      <div className="flex justify-end -mb-3"><BankGuide /></div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate("/app/finances")} className="inline-flex items-center gap-2 text-sm text-brand-green/60 hover:text-brand-greenDeep font-medium" data-testid="recon-back">
          <ArrowLeft className="w-4 h-4" /> Retour aux finances
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowRules(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors" data-testid="rules-btn">
            <Tags className="w-4 h-4" /> Règles de catégorisation
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><Banknote className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Rapprochement bancaire</h2>
          <p className="text-sm text-brand-green/55">Importez vos relevés (OFX/QIF) et pointez vos écritures automatiquement.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-brand-soft p-5" data-testid="import-manual-card">
          <h3 className="font-semibold text-brand-greenDeep mb-4">Import manuel d'un compte</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-brand-green/70 mb-1.5">Propriétaire</span>
              <select value={owner} onChange={(e) => setOwner(e.target.value)} className="inp" data-testid="import-owner">
                <option value="">— Sélectionner —</option>
                {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-brand-green/70 mb-1.5">Nom du compte (optionnel)</span>
              <input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="Ex. Compte courant BNP" className="inp" data-testid="import-account-name" />
            </label>
          </div>
          <label className="mt-3 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-brand-soft rounded-xl py-7 cursor-pointer hover:bg-brand-softer transition-colors" data-testid="import-drop">
            <Upload className="w-6 h-6 text-brand-green/40" />
            <span className="text-sm text-brand-green/70 font-medium">{file ? file.name : "Choisir un fichier .ofx ou .qif"}</span>
            <span className="text-xs text-brand-green/45">Relevé de compte au format bancaire OFX ou QIF</span>
            <input type="file" accept=".ofx,.qif" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="import-file" />
          </label>
          <button onClick={doImport} disabled={importing} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="import-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Importer
          </button>
        </div>

        <div className="bg-gradient-to-br from-brand-greenDeep to-brand-emerald text-white rounded-2xl p-5 flex flex-col justify-between" data-testid="powens-card">
          <div>
            {powens.connected ? (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 text-white text-xs font-semibold"><CircleDot className="w-3.5 h-3.5" /> Banque connectée</div>
            ) : (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-white/90 text-xs font-semibold"><Sparkles className="w-3.5 h-3.5" /> Synchronisation automatique</div>
            )}
            <h3 className="font-display text-lg font-semibold mt-3">Connexion bancaire (Powens)</h3>
            <p className="text-sm text-white/70 mt-1.5">{powens.connected ? "Vos comptes sont reliés. Relancez une synchronisation pour récupérer les dernières opérations." : "Connectez vos comptes bancaires pour importer et synchroniser vos opérations automatiquement."}</p>
            {powens.connected && powens.last_sync_at && (
              <p className="text-xs text-white/60 mt-2" data-testid="powens-last-sync">Dernière synchro : {new Date(powens.last_sync_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {powens.last_sync_transactions ?? 0} opération(s) importée(s)</p>
            )}
          </div>
          {powens.connected ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={syncPowens} disabled={powensBusy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="powens-sync-btn">
                {powensBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Synchroniser
              </button>
              <button onClick={connectBank} disabled={powensBusy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/15 text-white font-semibold text-sm disabled:opacity-60" data-testid="powens-add-btn">
                <Plus className="w-4 h-4" /> Ajouter une banque
              </button>
            </div>
          ) : (
            <button onClick={connectBank} disabled={powensBusy} className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white text-brand-greenDeep font-semibold text-sm hover:bg-white/90 transition-colors disabled:opacity-60" data-testid="powens-btn">
              {powensBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />} Connecter une banque
            </button>
          )}
        </div>
      </div>

      {powens.connected && connections.length > 0 && (
        <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden" data-testid="powens-connections">
          <div className="px-6 py-4 border-b border-brand-soft"><h3 className="font-semibold text-brand-greenDeep">Connexions bancaires (Powens)</h3></div>
          <div className="divide-y divide-brand-soft">
            {connections.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 px-6 py-3" data-testid={`connection-row-${c.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-brand-greenDeep truncate">{c.bank}</p>
                  <p className="text-xs text-brand-green/50">{c.last_update ? `Mise à jour : ${new Date(c.last_update).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : "—"}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {c.needs_action ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold" data-testid={`connection-state-${c.id}`}><AlertTriangle className="w-3.5 h-3.5" /> {c.state_label}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-emerald/10 text-brand-emerald text-xs font-semibold" data-testid={`connection-state-${c.id}`}><CircleDot className="w-3.5 h-3.5" /> {c.state_label}</span>
                  )}
                  {c.needs_action && (
                    <button onClick={() => reconnect(c.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-xs font-semibold transition-colors" data-testid={`connection-reconnect-${c.id}`}><RotateCcw className="w-3.5 h-3.5" /> Reconnecter</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden" data-testid="accounts-table">
        <div className="px-6 py-4 border-b border-brand-soft flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold text-brand-greenDeep">Comptes importés</h3>
          {owners.length > 0 && accounts.length > 0 && (
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className="inp max-w-[15rem]" data-testid="owner-filter">
              <option value="">Tous les propriétaires</option>
              {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
            </select>
          )}
        </div>
        {accounts.filter((a) => !ownerFilter || a.owner === ownerFilter).length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-brand-green/50">{accounts.length === 0 ? "Aucun compte importé. Utilisez l'import manuel ci-dessus." : "Aucun compte pour ce propriétaire."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-softer text-left text-brand-green/60">
                <tr>
                  <th className="px-6 py-3 font-medium">Compte</th>
                  <th className="px-6 py-3 font-medium">IBAN</th>
                  <th className="px-6 py-3 font-medium">Propriétaire</th>
                  <th className="px-6 py-3 font-medium text-right">Solde</th>
                  <th className="px-6 py-3 font-medium">Dernière synchro</th>
                  <th className="px-6 py-3 font-medium">Statut</th>
                  <th className="px-6 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-soft">
                {accounts.filter((a) => !ownerFilter || a.owner === ownerFilter).map((a) => (
                  <tr key={a.id} className="text-brand-green/80" data-testid={`account-row-${a.id}`}>
                    <td className="px-6 py-3 font-medium text-brand-greenDeep">{a.name}<span className={`block text-[11px] font-medium ${a.reconciled_count >= a.tx_count ? "text-brand-emerald" : "text-orange-600"}`}>{a.reconciled_count >= a.tx_count ? `✓ ${a.tx_count} pointées` : `${a.reconciled_count}/${a.tx_count} pointées · écart`}</span></td>
                    <td className="px-6 py-3 font-mono text-xs">{a.iban || "—"}</td>
                    <td className="px-6 py-3">{a.owner || "—"}</td>
                    <td className="px-6 py-3 text-right font-semibold text-brand-greenDeep">{fmt(a.balance)} €</td>
                    <td className="px-6 py-3">{fdate(a.last_sync)}</td>
                    <td className="px-6 py-3"><span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${a.status === "actif" ? "bg-brand-emerald/10 text-brand-emerald" : "bg-brand-soft text-brand-green/50"}`}><CircleDot className="w-3 h-3" /> {a.status === "actif" ? "Actif" : "Inactif"}</span></td>
                    <td className="px-6 py-3 text-right relative">
                      <button onClick={() => setMenuFor(menuFor === a.id ? null : a.id)} className="p-2 rounded-full hover:bg-brand-soft text-brand-green/50 hover:text-brand-greenDeep" data-testid={`account-action-${a.id}`}><MoreVertical className="w-4 h-4" /></button>
                      {menuFor === a.id && (
                        <div className="absolute right-6 top-12 z-20 w-52 bg-white rounded-xl border border-brand-soft shadow-lg py-1" data-testid={`account-menu-${a.id}`}>
                          <button onClick={() => { setReconAcc(a); setMenuFor(null); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-brand-greenDeep hover:bg-brand-softer" data-testid={`account-view-tx-${a.id}`}><ListChecks className="w-4 h-4" /> Voir les transactions</button>
                          <button onClick={() => { setMatchRulesAcc(a); setMenuFor(null); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-brand-greenDeep hover:bg-brand-softer" data-testid={`account-match-rules-${a.id}`}><Layers className="w-4 h-4" /> Règles de rapprochement</button>
                          <button onClick={() => { setEditAcc(a); setMenuFor(null); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-brand-greenDeep hover:bg-brand-softer" data-testid={`account-edit-${a.id}`}><Pencil className="w-4 h-4" /> Modifier</button>
                          <button onClick={() => delAcc(a)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-testid={`account-del-${a.id}`}><Trash2 className="w-4 h-4" /> Supprimer</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editAcc && <EditAccountModal account={editAcc} owners={owners} onClose={() => setEditAcc(null)} onSaved={() => { load(); setEditAcc(null); }} />}
      {reconAcc && <ReconcileModal account={reconAcc} properties={properties} cats={cats} onClose={() => { setReconAcc(null); load(); }} />}
      {showRules && <RulesModal cats={cats} onClose={() => setShowRules(false)} />}
      {matchRulesAcc && <MatchRulesModal account={matchRulesAcc} cats={cats} properties={properties} owners={owners} onClose={() => setMatchRulesAcc(null)} />}
      <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
    </div>
  );
}

function EditAccountModal({ account, owners, onClose, onSaved }) {
  const [form, setForm] = useState({ name: account.name || "", iban: account.iban || "", owner: account.owner || "", status: account.status || "actif" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/bank/accounts/${account.id}`, form);
      toast.success("Compte mis à jour");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="edit-account-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Modifier le compte</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1.5">Nom du compte</span><input value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="edit-name" /></label>
          <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1.5">IBAN</span><input value={form.iban} onChange={(e) => set("iban", e.target.value)} className="inp" data-testid="edit-iban" /></label>
          <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1.5">Propriétaire</span>
            <select value={form.owner} onChange={(e) => set("owner", e.target.value)} className="inp" data-testid="edit-owner">
              <option value="">— Sélectionner —</option>
              {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
            </select>
          </label>
          <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1.5">Statut</span>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="inp" data-testid="edit-status">
              <option value="actif">Actif</option>
              <option value="inactif">Inactif</option>
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-brand-soft text-brand-green/70 font-semibold text-sm hover:bg-brand-softer">Annuler</button>
            <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="edit-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer</button>
          </div>
        </div>
        <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>
    </div>
  );
}

function ReconcileModal({ account, properties, cats, onClose }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [creating, setCreating] = useState(null); // bt_id being created
  const [form, setForm] = useState({ category: "", property: "" });
  // search & filters
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fMin, setFMin] = useState("");
  const [fMax, setFMax] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  // multi-line grouping
  const [selected, setSelected] = useState([]);
  const [grouping, setGrouping] = useState(false);
  const [groupForm, setGroupForm] = useState({ category: "", property: "" });
  const [groupBusy, setGroupBusy] = useState(false);
  // journal
  const [showJournal, setShowJournal] = useState(false);
  const [journal, setJournal] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/bank/accounts/${account.id}/transactions`).then((r) => { setRows(r.data.transactions || []); setSummary(r.data.summary || null); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const loadJournal = () => {
    setJournalLoading(true);
    api.get(`/bank/accounts/${account.id}/journal`).then((r) => setJournal(r.data.journal || [])).catch(() => {}).finally(() => setJournalLoading(false));
  };
  const openJournal = () => { setShowJournal(true); loadJournal(); };

  const validate = async (bt) => {
    setBusyId(bt.id);
    try {
      await api.post(`/bank/transactions/${bt.id}/reconcile`, { matched_tx_id: bt.match?.id || "" });
      toast.success("Écriture pointée");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  const unreconcile = async (bt) => {
    if (bt.group_id && !window.confirm("Cette opération fait partie d'un groupe. Annuler le pointage défera tout le groupe et supprimera l'écriture groupée. Continuer ?")) return;
    setBusyId(bt.id);
    try {
      const res = await api.post(`/bank/transactions/${bt.id}/unreconcile`);
      toast.success(res.data.ungrouped ? `Groupe défait (${res.data.ungrouped} lignes)` : "Pointage annulé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  const point = async (ids) => {
    setBusyId(ids.length === 1 ? ids[0] : "__sel__");
    try {
      const res = await api.post("/bank/transactions/point", { bt_ids: ids });
      toast.success(`${res.data.pointed} opération(s) pointée(s) sans écriture`);
      setSelected([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  const validateAll = async () => {
    setBulkBusy(true);
    try {
      const res = await api.post(`/bank/accounts/${account.id}/reconcile-all`);
      toast.success(res.data.reconciled > 0 ? `${res.data.reconciled} ligne(s) pointée(s)` : "Aucune correspondance à valider");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBulkBusy(false); }
  };

  const startCreate = (bt) => {
    setCreating(bt.id);
    const isCredit = bt.amount >= 0;
    const list = isCredit ? cats.revenue : cats.expense;
    setForm({ category: bt.suggested_category || list[0] || "", property: "" });
  };

  const submitCreate = async (bt) => {
    if (!form.category) return toast.error("Choisissez une catégorie");
    setBusyId(bt.id);
    try {
      await api.post(`/bank/transactions/${bt.id}/create-entry`, { category: form.category, property: form.property, label: bt.label });
      toast.success("Écriture créée et pointée");
      setCreating(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  const toggleSelect = (bt) => {
    setSelected((p) => p.includes(bt.id) ? p.filter((x) => x !== bt.id) : [...p, bt.id]);
  };

  const selectedRows = rows.filter((r) => selected.includes(r.id));
  const selectedTotal = selectedRows.reduce((s, r) => s + (r.amount || 0), 0);
  const selMixedSign = new Set(selectedRows.map((r) => (r.amount >= 0 ? 1 : -1))).size > 1;
  const groupIsCredit = selectedTotal >= 0;
  const groupCatList = groupIsCredit ? cats.revenue : cats.expense;

  const startGroup = () => {
    if (selected.length < 2) return toast.error("Sélectionnez au moins 2 opérations");
    if (selMixedSign) return toast.error("Les opérations doivent être du même sens");
    setGroupForm({ category: groupCatList[0] || "", property: "" });
    setGrouping(true);
  };

  const submitGroup = async () => {
    if (!groupForm.category) return toast.error("Choisissez une catégorie");
    setGroupBusy(true);
    try {
      const res = await api.post(`/bank/accounts/${account.id}/group-reconcile`, {
        bt_ids: selected, category: groupForm.category, property: groupForm.property,
        label: `Groupe de ${selected.length} opérations`,
      });
      toast.success(`${res.data.count} opérations groupées et rapprochées`);
      setGrouping(false); setSelected([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setGroupBusy(false); }
  };

  const filtered = rows.filter((bt) => {
    if (search && !(bt.label || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (fStatus !== "all" && bt.status !== fStatus) return false;
    const abs = Math.abs(bt.amount || 0);
    if (fMin !== "" && abs < parseFloat(fMin)) return false;
    if (fMax !== "" && abs > parseFloat(fMax)) return false;
    const d = (bt.date || "").slice(0, 10);
    if (fFrom && d < fFrom) return false;
    if (fTo && d > fTo) return false;
    return true;
  });
  const hasFilters = search || fStatus !== "all" || fMin !== "" || fMax !== "" || fFrom || fTo;
  const clearFilters = () => { setSearch(""); setFStatus("all"); setFMin(""); setFMax(""); setFFrom(""); setFTo(""); };

  const pending = rows.filter((r) => r.status !== "reconciled").length;
  const greenCount = rows.filter((r) => r.status === "match").length;
  const actionLabel = { validate: "Pointage validé", unvalidate: "Pointage annulé", create: "Écriture créée", bulk: "Rapprochement en lot", group: "Rapprochement multi-lignes", receipt_paid: "Quittance payée (auto)" };
  const actionColor = { validate: "bg-brand-emerald/10 text-brand-emerald", unvalidate: "bg-red-100 text-red-700", create: "bg-blue-100 text-blue-700", bulk: "bg-brand-lime/40 text-brand-greenDeep", group: "bg-purple-100 text-purple-700", receipt_paid: "bg-emerald-100 text-emerald-800" };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reconcile-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Rapprochement — {account.name}</h3>
            <p className="text-xs text-brand-green/50">{pending} opération(s) à pointer · vert = correspondance, rouge = à créer</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openJournal} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold" data-testid="journal-btn"><History className="w-3.5 h-3.5" /> Journal</button>
            {greenCount > 0 && <button onClick={validateAll} disabled={bulkBusy} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60" data-testid="reconcile-all-btn">{bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />} Tout valider ({greenCount})</button>}
            <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
          </div>
        </div>
        {summary && (
          <div className="px-6 py-3 grid grid-cols-3 gap-3 border-b border-brand-soft bg-brand-softer/40" data-testid="reconcile-summary">
            <div><p className="text-[11px] text-brand-green/50">Solde bancaire</p><p className="font-semibold text-brand-greenDeep" data-testid="summary-balance">{fmt(summary.balance)} €</p></div>
            <div><p className="text-[11px] text-brand-green/50">Déjà pointé</p><p className="font-semibold text-brand-emerald">{summary.reconciled_count} op. · {fmt(summary.reconciled_amount)} €</p></div>
            <div><p className="text-[11px] text-brand-green/50">Reste à pointer</p><p className={`font-semibold ${summary.unreconciled_count ? "text-orange-600" : "text-brand-emerald"}`} data-testid="summary-remaining">{summary.unreconciled_count} op. · {fmt(summary.unreconciled_amount)} €</p></div>
          </div>
        )}
        {summary && (
          <div className="px-6 pb-3 bg-brand-softer/40">
            {summary.balanced ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-emerald/10 text-brand-emerald text-xs font-semibold" data-testid="ecart-badge"><Check className="w-3.5 h-3.5" /> Compte entièrement rapproché — soldes concordants</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold" data-testid="ecart-badge"><AlertTriangle className="w-3.5 h-3.5" /> Écart de solde : {fmt(summary.ecart)} € — {summary.unreconciled_count} opération(s) non pointée(s)</span>
            )}
          </div>
        )}

        {/* Search & filters */}
        <div className="px-6 py-3 border-b border-brand-soft space-y-2" data-testid="recon-filters">
          <div className="relative">
            <Search className="w-4 h-4 text-brand-green/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un libellé…" className="inp pl-9" data-testid="filter-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="inp" data-testid="filter-status">
              <option value="all">Tous statuts</option>
              <option value="match">Correspondance</option>
              <option value="unmatched">Non rapproché</option>
              <option value="reconciled">Rapproché</option>
            </select>
            <input value={fMin} onChange={(e) => setFMin(e.target.value)} type="number" placeholder="Montant min" className="inp" data-testid="filter-min" />
            <input value={fMax} onChange={(e) => setFMax(e.target.value)} type="number" placeholder="Montant max" className="inp" data-testid="filter-max" />
            <input value={fFrom} onChange={(e) => setFFrom(e.target.value)} type="date" className="inp" data-testid="filter-from" />
            <input value={fTo} onChange={(e) => setFTo(e.target.value)} type="date" className="inp" data-testid="filter-to" />
          </div>
          {hasFilters && (
            <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-brand-green/60 hover:text-brand-greenDeep font-medium" data-testid="filter-clear"><RotateCcw className="w-3 h-3" /> Réinitialiser les filtres ({filtered.length}/{rows.length})</button>
          )}
        </div>

        {/* Multi-line group bar */}
        {selected.length > 0 && (
          <div className="px-6 py-3 border-b border-brand-soft bg-purple-50 flex flex-wrap items-center justify-between gap-3" data-testid="group-bar">
            <div className="text-sm text-brand-greenDeep">
              <span className="font-semibold">{selected.length}</span> opération(s) · Total : <span className={`font-semibold ${selectedTotal >= 0 ? "text-brand-emerald" : "text-orange-600"}`}>{fmt(selectedTotal)} €</span>
              {selMixedSign && <span className="ml-2 text-red-600 text-xs">⚠ Sens mixte : sélectionnez des opérations de même sens</span>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelected([])} className="text-xs px-3 py-1.5 rounded-full border border-brand-soft text-brand-green/60 hover:bg-white" data-testid="group-clear">Annuler</button>
              <button onClick={() => point(selected)} disabled={busyId === "__sel__"} className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full border border-brand-greenDeep text-brand-greenDeep hover:bg-white font-semibold disabled:opacity-50" data-testid="point-selection-btn">{busyId === "__sel__" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stamp className="w-3.5 h-3.5" />} Pointer la sélection</button>
              <button onClick={startGroup} disabled={selMixedSign || selected.length < 2} className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold disabled:opacity-50" data-testid="group-start-btn"><Layers className="w-3.5 h-3.5" /> Grouper et rapprocher</button>
            </div>
          </div>
        )}
        {grouping && (
          <div className="px-6 py-3 border-b border-brand-soft bg-purple-50/60 grid sm:grid-cols-3 gap-2 items-end" data-testid="group-form">
            <label className="block"><span className="block text-[11px] font-medium text-brand-green/70 mb-1">Type</span><input readOnly value={groupIsCredit ? "Revenu" : "Dépense"} className="inp bg-white" /></label>
            <label className="block"><span className="block text-[11px] font-medium text-brand-green/70 mb-1">Catégorie</span>
              <select value={groupForm.category} onChange={(e) => setGroupForm((s) => ({ ...s, category: e.target.value }))} className="inp" data-testid="group-cat">
                {groupCatList.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block"><span className="block text-[11px] font-medium text-brand-green/70 mb-1">Bien</span>
              <select value={groupForm.property} onChange={(e) => setGroupForm((s) => ({ ...s, property: e.target.value }))} className="inp" data-testid="group-prop">
                <option value="">— Aucun —</option>
                {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </label>
            <div className="sm:col-span-3 flex justify-end gap-2">
              <button onClick={() => setGrouping(false)} className="text-xs px-3 py-1.5 rounded-full border border-brand-soft text-brand-green/60 hover:bg-white">Annuler</button>
              <button onClick={submitGroup} disabled={groupBusy} className="inline-flex items-center gap-1 text-xs px-4 py-1.5 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold disabled:opacity-60" data-testid="group-submit-btn">{groupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Créer l'écriture groupée ({fmt(Math.abs(selectedTotal))} €)</button>
            </div>
          </div>
        )}

        <div className="p-4 space-y-2">
          {loading && <div className="py-10 text-center text-brand-green/50"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!loading && filtered.length === 0 && <p className="py-8 text-center text-sm text-brand-green/50">{rows.length === 0 ? "Aucune opération." : "Aucune opération ne correspond aux filtres."}</p>}
          {filtered.map((bt) => {
            const green = bt.status === "reconciled" || bt.status === "match";
            const isCredit = bt.amount >= 0;
            const catList = isCredit ? cats.revenue : cats.expense;
            const selectable = bt.status !== "reconciled";
            const isSel = selected.includes(bt.id);
            return (
              <div key={bt.id} className={`rounded-xl border p-3 ${isSel ? "border-purple-400 bg-purple-50" : bt.status === "reconciled" ? "border-brand-soft bg-brand-softer/40" : green ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`} data-testid={`bt-row-${bt.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {selectable && (
                      <input type="checkbox" checked={isSel} onChange={() => toggleSelect(bt)} className="w-4 h-4 shrink-0 accent-purple-600 cursor-pointer" data-testid={`bt-select-${bt.id}`} />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-brand-greenDeep truncate">{bt.label}{bt.group_id ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold" data-testid={`bt-grouped-${bt.id}`}>groupé</span> : null}</p>
                      <p className="text-xs text-brand-green/50">{fdate(bt.date)} · <span className={isCredit ? "text-brand-emerald" : "text-orange-600"}>{fmt(bt.amount)} €</span>{bt.match ? ` · correspond à « ${bt.match.category || bt.match.label} »` : ""}</p>
                      {bt.match && bt.match.rule_learned && <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-full bg-brand-lime/40 text-brand-greenDeep font-semibold" title={bt.match.rule_name ? `Règle apprise : ${bt.match.rule_name}` : "Règle apprise"} data-testid={`bt-rule-learned-${bt.id}`}><Sparkles className="w-3 h-3" /> Proposé par règle apprise</span>}
                      {bt.match && bt.match.by_rule && !bt.match.rule_learned && <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold" title={bt.match.rule_name ? `Règle : ${bt.match.rule_name}` : "Règle de rapprochement"} data-testid={`bt-rule-manual-${bt.id}`}><Layers className="w-3 h-3" /> Proposé par règle</span>}
                      {bt.match && bt.match.confidence && CONF[bt.match.confidence] && <span className={`inline-flex items-center gap-1 mt-1 ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${CONF[bt.match.confidence].cls}`} title="Niveau de fiabilité de la suggestion" data-testid={`bt-confidence-${bt.id}`}><CircleDot className="w-3 h-3" /> {CONF[bt.match.confidence].label}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {bt.status === "reconciled" && <><span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-brand-emerald/10 text-brand-emerald font-semibold" data-testid={`bt-done-${bt.id}`}><Check className="w-3.5 h-3.5" /> {bt.manual_pointed ? "Pointée (sans écriture)" : "Pointée"}</span><button onClick={() => unreconcile(bt)} disabled={busyId === bt.id} title="Annuler le pointage" className="p-1.5 rounded-full hover:bg-red-50 text-brand-green/40 hover:text-red-600 disabled:opacity-60" data-testid={`bt-unreconcile-${bt.id}`}>{busyId === bt.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}</button></>}
                    {bt.status === "match" && <button onClick={() => validate(bt)} disabled={busyId === bt.id} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60" data-testid={`bt-validate-${bt.id}`}>{busyId === bt.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Valider</button>}
                    {bt.status === "unmatched" && creating !== bt.id && <button onClick={() => point([bt.id])} disabled={busyId === bt.id} title="Marquer comme pointée sans créer d'écriture" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-brand-greenDeep text-brand-greenDeep hover:bg-white font-semibold disabled:opacity-60" data-testid={`bt-point-${bt.id}`}>{busyId === bt.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stamp className="w-3.5 h-3.5" />} Pointer</button>}
                    {bt.status === "unmatched" && creating !== bt.id && <button onClick={() => startCreate(bt)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid={`bt-create-${bt.id}`}><Plus className="w-3.5 h-3.5" /> Créer l'écriture</button>}
                  </div>
                </div>
                {creating === bt.id && (
                  <div className="mt-3 pt-3 border-t border-red-200 grid sm:grid-cols-3 gap-2 items-end" data-testid={`bt-create-form-${bt.id}`}>
                    <label className="block"><span className="block text-[11px] font-medium text-brand-green/70 mb-1">Type</span><input readOnly value={isCredit ? "Revenu" : "Dépense"} className="inp bg-brand-softer" /></label>
                    <label className="block"><span className="block text-[11px] font-medium text-brand-green/70 mb-1">Catégorie</span>
                      <select value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} className="inp" data-testid={`bt-cat-${bt.id}`}>
                        {catList.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <label className="block"><span className="block text-[11px] font-medium text-brand-green/70 mb-1">Bien</span>
                      <select value={form.property} onChange={(e) => setForm((s) => ({ ...s, property: e.target.value }))} className="inp" data-testid={`bt-prop-${bt.id}`}>
                        <option value="">— Aucun —</option>
                        {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                      </select>
                    </label>
                    <div className="sm:col-span-3 flex justify-end gap-2">
                      <button onClick={() => setCreating(null)} className="text-xs px-3 py-1.5 rounded-full border border-brand-soft text-brand-green/60 hover:bg-brand-softer">Annuler</button>
                      <button onClick={() => submitCreate(bt)} disabled={busyId === bt.id} className="inline-flex items-center gap-1 text-xs px-4 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold disabled:opacity-60" data-testid={`bt-create-submit-${bt.id}`}>{busyId === bt.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Valider l'écriture</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <style>{`.inp{width:100%;padding:0.5rem 0.75rem;border-radius:0.7rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.8rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>

      {showJournal && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setShowJournal(false); }}>
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="journal-modal">
            <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><History className="w-5 h-5" /> Journal du rapprochement</h3>
                <p className="text-xs text-brand-green/50">Historique des pointages du compte « {account.name} »</p>
              </div>
              <button onClick={() => setShowJournal(false)} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-2">
              {journalLoading && <div className="py-10 text-center text-brand-green/50"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
              {!journalLoading && journal.length === 0 && <p className="py-8 text-center text-sm text-brand-green/50">Aucune action enregistrée pour ce compte.</p>}
              {journal.map((j) => (
                <div key={j.id} className="flex items-start gap-3 rounded-xl border border-brand-soft p-3" data-testid={`journal-row-${j.id}`}>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${actionColor[j.action] || "bg-brand-soft text-brand-green/60"}`}>{actionLabel[j.action] || j.action}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-brand-greenDeep truncate">{j.label || "—"}</p>
                    <p className="text-[11px] text-brand-green/50">{j.detail}{j.tx_count > 1 ? ` · ${j.tx_count} lignes` : ""}{j.amount ? ` · ${fmt(j.amount)} €` : ""}</p>
                  </div>
                  <span className="text-[11px] text-brand-green/40 shrink-0">{new Date(j.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function RulesModal({ cats, onClose }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const allCats = [...(cats.revenue || []), ...(cats.expense || [])];

  const load = () => {
    setLoading(true);
    api.get("/bank/category-rules").then((r) => setRules(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (rule, category) => {
    setBusyId(rule.id);
    try {
      await api.put(`/bank/category-rules/${rule.id}`, { category });
      setRules((p) => p.map((r) => (r.id === rule.id ? { ...r, category } : r)));
      toast.success("Règle mise à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  const del = async (rule) => {
    if (!window.confirm("Supprimer cette règle ?")) return;
    setBusyId(rule.id);
    try {
      await api.delete(`/bank/category-rules/${rule.id}`);
      setRules((p) => p.filter((r) => r.id !== rule.id));
      toast.success("Règle supprimée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="rules-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Tags className="w-5 h-5" /> Règles de catégorisation</h3>
            <p className="text-xs text-brand-green/50">Catégories mémorisées par libellé — appliquées automatiquement aux futures opérations.</p>
          </div>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {loading && <div className="py-10 text-center text-brand-green/50"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!loading && rules.length === 0 && <p className="py-8 text-center text-sm text-brand-green/50">Aucune règle. Elles se créent automatiquement lorsque vous catégorisez une opération importée.</p>}
          {rules.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-xl border border-brand-soft p-3" data-testid={`rule-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-brand-greenDeep truncate">« {r.key} »</p>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${r.type === "Revenu" ? "bg-brand-emerald/10 text-brand-emerald" : "bg-orange-100 text-orange-700"}`}>{r.type || "—"}</span>
              </div>
              <select value={r.category} onChange={(e) => save(r, e.target.value)} className="inp max-w-[16rem]" data-testid={`rule-cat-${r.id}`}>
                {[r.category, ...allCats.filter((c) => c !== r.category)].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={() => del(r)} disabled={busyId === r.id} className="p-2 rounded-full hover:bg-red-50 text-brand-green/50 hover:text-red-600 disabled:opacity-60" data-testid={`rule-del-${r.id}`}>{busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</button>
            </div>
          ))}
        </div>
        <style>{`.inp{width:100%;padding:0.5rem 0.75rem;border-radius:0.7rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.8rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>
    </div>
  );
}

function RulePreviewModal({ preview, setPreview, onConfirm, busy, isNew }) {
  const { items, sel } = preview;
  const toggle = (id) => setPreview((p) => ({ ...p, sel: p.sel.includes(id) ? p.sel.filter((x) => x !== id) : [...p.sel, id] }));
  const all = sel.length === items.length;
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setPreview(null); }}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="rule-preview-modal">
        <div className="p-6 pb-4 border-b border-brand-soft flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Eye className="w-5 h-5" /> Aperçu des rapprochements</h3>
            <p className="text-xs text-brand-green/50" data-testid="rule-preview-count">{items.length} rapprochement(s) prévu(s) · {sel.length} sélectionné(s) · rien n'est enregistré avant validation</p>
          </div>
          <button onClick={() => setPreview(null)} className="text-brand-green/40 hover:text-brand-greenDeep" data-testid="rule-preview-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-2 flex-1">
          {items.length === 0 ? <p className="py-8 text-center text-sm text-brand-green/50" data-testid="rule-preview-empty">Aucune opération non rapprochée ne correspond à cette règle.</p> : (
            <label className="flex items-center gap-2 text-xs text-brand-green/70 px-1 cursor-pointer"><input type="checkbox" checked={all} onChange={() => setPreview((p) => ({ ...p, sel: all ? [] : items.map((i) => i.bt_id) }))} className="accent-brand-greenDeep" data-testid="rule-preview-all" /> Tout sélectionner</label>
          )}
          {items.map((i) => (
            <label key={i.bt_id} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${sel.includes(i.bt_id) ? "border-brand-greenDeep/40 bg-brand-softer/40" : "border-brand-soft opacity-60"}`} data-testid={`rule-preview-item-${i.bt_id}`}>
              <input type="checkbox" checked={sel.includes(i.bt_id)} onChange={() => toggle(i.bt_id)} className="mt-1 accent-brand-greenDeep" data-testid={`rule-preview-check-${i.bt_id}`} />
              <div className="min-w-0 flex-1 text-sm">
                <p className="font-medium text-brand-greenDeep truncate">{i.label}</p>
                <p className="text-xs text-brand-green/55">{fdate(i.date)} · <span className={i.amount >= 0 ? "text-brand-emerald" : "text-orange-600"}>{fmt(i.amount)} €</span></p>
                <p className="text-xs mt-1 text-brand-greenDeep">→ {i.kind === "receipt" ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-lime/40 font-semibold mr-1">Quittance → Payé</span> : null}{i.target_label}{i.target_category && i.kind !== "receipt" ? ` · ${i.target_category}` : ""}{i.target_date ? ` · ${fdate(i.target_date)}` : ""} · {fmt(i.target_amount)} €{i.target_property ? ` · ${i.target_property}` : ""}</p>
              </div>
            </label>
          ))}
        </div>
        <div className="p-4 border-t border-brand-soft flex justify-end gap-2">
          <button onClick={() => setPreview(null)} className="text-sm px-4 py-2 rounded-full border border-brand-soft text-brand-green/60 hover:bg-brand-softer" data-testid="rule-preview-cancel">Annuler</button>
          <button onClick={onConfirm} disabled={busy || (!isNew && sel.length === 0)} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold disabled:opacity-60" data-testid="rule-preview-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {isNew ? `Enregistrer et appliquer (${sel.length})` : `Appliquer la sélection (${sel.length})`}</button>
        </div>
      </div>
    </div>
  );
}


function MatchRulesModal({ account, cats, properties, owners, onClose }) {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const EMPTY = { name: "", label_keyword: "", amount: "", amount_tolerance: "", date_tolerance_days: 3, tenant: "", category: "", property: "", type: "", auto: true, key_mode: "simple" };
  const [suggs, setSuggs] = useState([]);
  const loadSuggs = () => api.get(`/bank/match-rules/suggestions?account_id=${account.id}`).then((r) => setSuggs(r.data || [])).catch(() => {});
  const appliedMsg = (a) => (a ? ` · ${a.auto_reconciled || 0} opération(s) rapprochée(s) par les règles, dont ${a.new_reconciled ?? a.auto_reconciled ?? 0} nouvelle(s) et ${a.receipts_paid || 0} quittance(s) payée(s)` : "");
  const editSugg = (g) => { setEditingId(null); setForm({ ...EMPTY, name: `Règle ${g.label_keyword}`.slice(0, 60), label_keyword: g.label_keyword, amount: String(g.amount), date_tolerance_days: g.date_tolerance_days, tenant: g.tenant, category: g.category, property: g.property, type: g.type, key_mode: "simple" }); document.querySelector('[data-testid="match-rules-modal"]')?.scrollTo?.({ top: 0, behavior: "smooth" }); };
  const acceptSugg = async (g) => {
    try { const r = await api.post("/bank/match-rules", { account_id: account.id, name: `Règle ${g.label_keyword}`.slice(0, 60), label_keyword: g.label_keyword, amount: g.amount, amount_tolerance: 0, date_tolerance_days: g.date_tolerance_days, tenant: g.tenant, category: g.category, property: g.property, type: g.type, auto: true, key_mode: "simple" }); toast.success(`Règle créée${appliedMsg(r.data.applied)}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const dismissSugg = async (g) => { await api.post("/bank/match-rules/suggestions/dismiss", { account_id: account.id, key: g.key }).catch(() => {}); loadSuggs(); };
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const allCats = [...(cats.revenue || []), ...(cats.expense || [])];

  const loadStats = () => api.get(`/bank/match-rules/stats?account_id=${account.id}`).then((r) => setStats(r.data)).catch(() => {});
  const load = () => {
    loadStats(); loadSuggs();
    return api.get(`/bank/match-rules?account_id=${account.id}`).then((r) => setRules(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "", label_keyword: r.label_keyword || "",
      amount: r.amount == null ? "" : String(r.amount),
      amount_tolerance: r.amount_tolerance ? String(r.amount_tolerance) : "",
      date_tolerance_days: r.date_tolerance_days ?? 3,
      tenant: r.tenant || "", category: r.category || "", property: r.property || "", type: r.type || "", auto: r.auto !== false, key_mode: r.key_mode || "simple",
    });
    document.querySelector('[data-testid="match-rules-modal"]')?.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const [preview, setPreview] = useState(null);
  const validForm = () => {
    if (!form.label_keyword.trim() && !form.tenant.trim() && !form.category.trim() && !form.property.trim()) {
      toast.error("Renseignez au moins un critère (mot-clé, locataire, catégorie ou bien)"); return false;
    }
    return true;
  };
  const buildPayload = () => ({
      account_id: account.id,
      name: form.name, label_keyword: form.label_keyword,
      amount: form.amount === "" ? null : Number(form.amount),
      amount_tolerance: form.amount_tolerance === "" ? 0 : Number(form.amount_tolerance),
      date_tolerance_days: Number(form.date_tolerance_days) || 3,
      tenant: form.tenant, category: form.category, property: form.property, type: form.type, auto: form.auto, key_mode: form.key_mode,
  });
  const openPreview = async (body, ruleId) => {
    try {
      const r = await api.post("/bank/match-rules/preview", body);
      const items = r.data.items || [];
      setPreview({ items, sel: items.map((i) => i.bt_id), ruleId });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const previewForm = () => { if (validForm()) openPreview({ ...buildPayload(), rule_id: "" }, null); };
  const previewRule = (r) => openPreview({ rule_id: r.id }, r.id);
  const confirmPreview = async () => {
    setSaving(true);
    try {
      let ruleId = preview.ruleId;
      if (!ruleId) {
        if (editingId) { await api.put(`/bank/match-rules/${editingId}?apply=false`, buildPayload()); ruleId = editingId; }
        else ruleId = (await api.post("/bank/match-rules?apply=false", buildPayload())).data.id;
        cancelEdit();
      }
      const res = preview.sel.length ? (await api.post("/bank/match-rules/apply-selected", { rule_id: ruleId, bt_ids: preview.sel })).data : null;
      toast.success(`Règle enregistrée${appliedMsg(res || { auto_reconciled: 0 })}`);
      setPreview(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const save = async () => {
    if (!validForm()) return;
    setSaving(true);
    const payload = buildPayload();
    try {
      if (editingId) {
        const r = await api.put(`/bank/match-rules/${editingId}`, payload);
        toast.success(`Règle mise à jour${appliedMsg(r.data.applied)}`);
      } else {
        const r = await api.post("/bank/match-rules", payload);
        toast.success(`Règle de rapprochement créée${appliedMsg(r.data.applied)}`);
      }
      cancelEdit(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm("Supprimer cette règle ?")) return;
    try { await api.delete(`/bank/match-rules/${r.id}`); if (editingId === r.id) cancelEdit(); load(); toast.success("Règle supprimée"); }
    catch { toast.error("Erreur"); }
  };

  const toggle = async (r) => {
    try { const res = await api.post(`/bank/match-rules/${r.id}/toggle`); load(); toast.success(res.data.enabled ? `Règle activée${appliedMsg(res.data.applied)}` : "Règle désactivée"); }
    catch { toast.error("Erreur"); }
  };

  const [applying, setApplying] = useState(false);
  const applyAll = async () => {
    setApplying(true);
    try { const res = await api.post(`/bank/match-rules/apply?account_id=${account.id}`); load(); toast.success(`Règles appliquées${appliedMsg(res.data)}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setApplying(false); }
  };

  const convert = async (r) => {
    try { await api.post(`/bank/match-rules/${r.id}/convert`); load(); toast.success("Règle convertie en règle manuelle"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="match-rules-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Layers className="w-5 h-5" /> Règles de rapprochement</h3>
            <p className="text-xs text-brand-green/50 mt-1">Compte « {account.name} » — associez automatiquement une opération bancaire à une écriture selon le libellé, le locataire, le montant ou la date. Les règles sont propres à ce compte. Les pointages validés créent aussi des règles apprises (mode auto-apprentissage).</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
              {rules.length > 0 && (
                <button onClick={applyAll} disabled={applying} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-greenDeep text-brand-greenDeep hover:bg-brand-softer text-xs font-semibold disabled:opacity-60" data-testid="mr-apply-all">
                  {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Appliquer les règles
                </button>
              )}
            <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep" data-testid="match-rules-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {stats && (
            <div className="rounded-2xl border border-brand-soft p-4 bg-gradient-to-br from-brand-greenDeep to-brand-emerald text-white" data-testid="match-rules-stats">
              <p className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Fiabilité des règles</p>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div><p className="text-2xl font-display font-semibold" data-testid="stats-auto-rate">{stats.auto_rate}%</p><p className="text-[11px] text-white/70">Rapprochement auto</p></div>
                <div><p className="text-2xl font-display font-semibold" data-testid="stats-auto-count">{stats.auto_reconciled}</p><p className="text-[11px] text-white/70">Opérations auto-pointées</p></div>
                <div><p className="text-2xl font-display font-semibold" data-testid="stats-active-rules">{stats.active_rules}/{stats.total_rules}</p><p className="text-[11px] text-white/70">Règles actives</p></div>
              </div>
              {stats.top_rules && stats.top_rules.length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/15">
                  <p className="text-[11px] text-white/70 mb-2 flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Règles les plus utilisées</p>
                  <div className="space-y-1.5" data-testid="stats-top-rules">
                    {stats.top_rules.map((t, i) => (
                      <div key={t.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`stats-top-rule-${t.id}`}>
                        <span className="truncate flex items-center gap-1.5"><span className="text-white/50 w-4 shrink-0">{i + 1}.</span>{t.name}{t.learned && <Sparkles className="w-3 h-3 text-brand-lime shrink-0" />}</span>
                        <span className="shrink-0 px-2 py-0.5 rounded-full bg-white/15 text-xs font-semibold">{t.hits} usage{t.hits > 1 ? "s" : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-brand-soft p-4 space-y-3 bg-brand-softer/30">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-brand-greenDeep">{editingId ? "Modifier la règle" : "Nouvelle règle"}</p>
              {editingId && <button onClick={cancelEdit} className="text-xs text-brand-green/60 hover:text-brand-greenDeep font-medium" data-testid="mr-cancel-edit">Annuler la modification</button>}
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="mr-key-mode">
              {[["simple", "Clé simplifiée", "chaque mot du mot-clé présent dans le libellé (%mot%)"], ["complete", "Clé complète", "mot-clé présent tel quel, en un seul bloc"]].map(([k, l, h]) => (
                <button key={k} type="button" onClick={() => set("key_mode", k)} title={h} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${form.key_mode === k ? "bg-brand-greenDeep text-white" : "bg-white border border-brand-soft text-brand-green/70"}`} data-testid={`mr-key-${k}`}>{l}</button>
              ))}
              <span className="text-[11px] text-brand-green/50">{form.key_mode === "complete" ? "Mot-clé présent tel quel (expression d'un bloc)" : "Tous les mots du mot-clé présents, dans n'importe quel ordre"} · catégorie, bien, montant exact et tolérance de date</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Nom (optionnel)</span><input value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" placeholder="Ex : Loyer Dupont" data-testid="mr-name" /></label>
              <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Mot-clé dans le libellé bancaire</span><input value={form.label_keyword} onChange={(e) => set("label_keyword", e.target.value)} className="inp" placeholder="Ex : dupont, loyer…" data-testid="mr-keyword" /></label>
              <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Locataire</span><input value={form.tenant} onChange={(e) => set("tenant", e.target.value)} className="inp" placeholder="Nom du locataire" data-testid="mr-tenant" /></label>
              <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Catégorie</span>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className="inp" data-testid="mr-category">
                  <option value="">— Toutes —</option>
                  {allCats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Bien</span>
                <select value={form.property} onChange={(e) => set("property", e.target.value)} className="inp" data-testid="mr-property">
                  <option value="">— Tous —</option>
                  {(properties || []).map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </label>
              <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Montant exact (optionnel)</span><input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="inp" placeholder="Laisser vide = auto" data-testid="mr-amount" /></label>
              <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Tolérance montant (€)</span><input type="number" value={form.amount_tolerance} onChange={(e) => set("amount_tolerance", e.target.value)} className="inp" placeholder="0" data-testid="mr-amount-tol" /></label>
              <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Tolérance de date (jours)</span><input type="number" value={form.date_tolerance_days} onChange={(e) => set("date_tolerance_days", e.target.value)} className="inp" data-testid="mr-date-tol" /></label>
            </div>
            <div className="flex flex-wrap gap-2">
            <button onClick={previewForm} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-greenDeep text-brand-greenDeep hover:bg-white font-semibold text-sm disabled:opacity-60" data-testid="mr-preview"><Eye className="w-4 h-4" /> Aperçu</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm disabled:opacity-60" data-testid="mr-create">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editingId ? "Enregistrer les modifications" : "Ajouter la règle"}
            </button>
            </div>
          </div>

          <div>
            {suggs.length > 0 && (
              <div className="mb-4 rounded-2xl border border-brand-lime bg-brand-lime/15 p-4 space-y-2" data-testid="mr-suggestions">
                <p className="text-sm font-semibold text-brand-greenDeep">{suggs.length} règle(s) suggérée(s) : opérations récurrentes non couvertes</p>
                {suggs.map((g) => (
                  <div key={g.key} className="bg-white rounded-xl border border-brand-soft p-3 text-sm flex flex-wrap items-center gap-2" data-testid={`mr-sugg-${g.key}`}>
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-brand-greenDeep">« {g.label_keyword} » · {g.type === "Revenu" ? "+" : "-"}{g.amount.toLocaleString("fr-FR")} € · {g.occurrences} opérations</p>
                      <p className="text-xs text-brand-green/55">{[g.category, g.property, g.tenant].filter(Boolean).join(" · ") || "Catégorie et bien à compléter"} · tolérance {g.date_tolerance_days} j · {g.unreconciled} non rapprochée(s)</p>
                    </div>
                    <button onClick={() => acceptSugg(g)} className="px-3 py-1.5 rounded-full bg-brand-greenDeep text-white text-xs font-semibold" data-testid={`mr-sugg-accept-${g.key}`}>Valider</button>
                    <button onClick={() => editSugg(g)} className="px-3 py-1.5 rounded-full border border-brand-soft text-xs" data-testid={`mr-sugg-edit-${g.key}`}>Modifier</button>
                    <button onClick={() => dismissSugg(g)} className="px-3 py-1.5 rounded-full text-xs text-brand-green/50 hover:text-red-600" data-testid={`mr-sugg-dismiss-${g.key}`}>Ignorer</button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-sm font-semibold text-brand-greenDeep mb-2">Règles existantes ({rules.length})</p>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-brand-green/40" /></div>
            ) : rules.length === 0 ? (
              <p className="text-sm text-brand-green/50 py-6 text-center rounded-2xl border border-dashed border-brand-soft">Aucune règle. Créez-en une ci-dessus, ou validez des pointages : les associations seront apprises automatiquement.</p>
            ) : (
              <div className="space-y-2" data-testid="match-rules-list">
                {rules.map((r) => {
                  const disabled = r.enabled === false;
                  return (
                  <div key={r.id} className={`flex items-start justify-between gap-3 p-3 rounded-xl border ${editingId === r.id ? "border-brand-greenDeep ring-1 ring-brand-greenDeep/30" : disabled ? "border-brand-soft bg-brand-softer/40 opacity-60" : "border-brand-soft"}`} data-testid={`match-rule-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-brand-greenDeep flex items-center gap-2 flex-wrap">
                        {r.name}
                        {r.learned && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-brand-lime/40 text-brand-greenDeep font-semibold"><Sparkles className="w-3 h-3" /> Apprise{r.hits ? ` ×${r.hits}` : ""}</span>}
                        {!r.learned && r.hits > 0 && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold" title="Nombre d'opérations pointées par cette règle"><Award className="w-3 h-3" /> {r.hits} usage{r.hits > 1 ? "s" : ""}</span>}
                        {disabled && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">Désactivée</span>}
                      </p>
                      <p className="text-xs text-brand-green/55 mt-0.5">
                        {[r.label_keyword && `${r.key_mode === "complete" ? "clé complète" : "clé simplifiée"} « ${r.label_keyword} »`, r.tenant && `locataire : ${r.tenant}`, r.category && `catégorie : ${r.category}`, r.property && `bien : ${r.property}`, (r.amount != null) && `montant : ${r.amount} €`, `±${r.date_tolerance_days ?? 3} j`].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button onClick={() => previewRule(r)} title="Aperçu des rapprochements prévus" className="p-1.5 rounded-full hover:bg-brand-softer text-brand-green/50 hover:text-brand-greenDeep transition-colors" data-testid={`match-rule-preview-${r.id}`}><Eye className="w-4 h-4" /></button>
                      <button onClick={() => startEdit(r)} title="Modifier cette règle" className="p-1.5 rounded-full hover:bg-brand-softer text-brand-green/50 hover:text-brand-greenDeep transition-colors" data-testid={`match-rule-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => toggle(r)} className={`text-[11px] px-2.5 py-1 rounded-full font-semibold transition-colors ${disabled ? "bg-brand-emerald/10 text-brand-emerald hover:bg-brand-emerald/20" : "bg-brand-softer text-brand-green/70 hover:bg-brand-soft"}`} data-testid={`match-rule-toggle-${r.id}`}>{disabled ? "Activer" : "Désactiver"}</button>
                      {r.learned && <button onClick={() => convert(r)} title="Convertir en règle manuelle (ne sera plus modifiée par l'apprentissage)" className="text-[11px] px-2.5 py-1 rounded-full font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" data-testid={`match-rule-convert-${r.id}`}>Convertir</button>}
                      <button onClick={() => remove(r)} className="p-1.5 rounded-full hover:bg-red-50 text-brand-green/40 hover:text-red-600" data-testid={`match-rule-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <style>{`.inp{width:100%;padding:0.5rem 0.75rem;border-radius:0.7rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.8rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>
      {preview && <RulePreviewModal preview={preview} setPreview={setPreview} onConfirm={confirmPreview} busy={saving} isNew={!preview.ruleId} />}
    </div>
  );
}
